import { IApiBaseResponse } from "models/interface";
import { ApiService } from "modules/shared/services";
import {
  IResponseQuestionSnapshot,
  IResponseRankingProjectListData,
  IRankingProjectExecution,
  IResponseRankingTypeListData,
  ICreateEditRankingProjectPayload,
  IResponseCreateEditRankingProject,
  IResponseProjectRecordDateRange,
  IRankingProjectQuestion,
  IResponseRankingSnapshot,
  IRequestRankingSnapshot,
  IResponseRankingEquations,
  IResponseCopyRankingProject,
} from "../models/interface";
import { RoutesEnum } from "models/enums";
import {
  IRankingTemplateFormData,
  IResponseCreateEditRankingTemplate,
  IResponseRankingTemplateListData,
} from "../models/interface/rankingSetup.interface";
import { IEquationAnswer } from "../store";

export class RankingProjectService {
  public async getQuestionSnapshot(
    project_slug: string,
    ranking_project_id: number,
    refresh?: boolean,
  ) {
    const formattedApiUrl = RoutesEnum.ORG_RANKING_PROJECT_GET_QUESTION.replace(
      "{project_slug}",
      project_slug,
    ).replace("{ranking_project_id}", `${ranking_project_id}`);
    return (await new ApiService().privateClient()).get<
      IApiBaseResponse<IResponseQuestionSnapshot>
    >(formattedApiUrl, refresh ? { params: { refresh: true } } : undefined);
  }
  public async getRankingTemplates() {
    return (await new ApiService().privateClient()).get<
      IApiBaseResponse<IResponseRankingTemplateListData>
    >(RoutesEnum.ORG_RANKING_PROJECT_GET_RANKING_TEMPLATES);
  }

  public async createRankingTemplate(payload: IRankingTemplateFormData) {
    return (await new ApiService().privateClient()).post<
      IApiBaseResponse<IResponseCreateEditRankingTemplate>
    >(RoutesEnum.ORG_RANKING_PROJECT_CREATE_RANKING_TEMPLATE, payload);
  }

  public async editRankingTemplate(payload: IRankingTemplateFormData) {
    return (await new ApiService().privateClient()).post<
      IApiBaseResponse<IResponseCreateEditRankingTemplate>
    >(RoutesEnum.ORG_RANKING_PROJECT_EDIT_RANKING_TEMPLATE, payload);
  }

  public async deleteRankingTemplate(payload: { id: number }) {
    return (await new ApiService().privateClient()).delete<
      IApiBaseResponse<IResponseCreateEditRankingTemplate>
    >(RoutesEnum.ORG_RANKING_PROJECT_DELETE_RANKING_TEMPLATE, {
      data: { ...payload },
    });
  }

  public async getRecordAnswers(
    project_slug: string,
    ranking_project_id: number,
    record_id: number,
  ) {
    const formattedApiUrl =
      RoutesEnum.ORG_RANKING_PROJECT_GET_RECORD_ANSWERS.replace(
        "{project_slug}",
        `${project_slug}`,
      )
        .replace("{ranking_project_id}", `${ranking_project_id}`)
        .replace("{record_id}", `${record_id}`);
    return (await new ApiService().privateClient()).get<
      IApiBaseResponse<{ answers: IEquationAnswer[] }>
    >(formattedApiUrl);
  }

  public async getRankingSnapshot(
    project_slug: string,
    ranking_project_id: number,
    query: IRequestRankingSnapshot,
  ) {
    const formattedApiUrl = RoutesEnum.ORG_RANKING_PROJECT_GET_SNAPSHOT.replace(
      "{project_slug}",
      project_slug,
    ).replace("{ranking_project_id}", `${ranking_project_id}`);
    return (await new ApiService().privateClient()).get<
      IApiBaseResponse<IResponseRankingSnapshot>
    >(formattedApiUrl, { params: query });
  }

  public async getRankingEquations(
    project_slug: string,
    ranking_project_id: number,
  ) {
    const formattedApiUrl =
      RoutesEnum.ORG_RANKING_PROJECT_GET_EQUATIONS.replace(
        "{project_slug}",
        project_slug,
      ).replace("{ranking_project_id}", `${ranking_project_id}`);
    return (await new ApiService().privateClient()).get<
      IApiBaseResponse<IResponseRankingEquations>
    >(formattedApiUrl);
  }

  public async saveRankingEquation(
    project_slug: string,
    ranking_project_id: number,
    rank_name: string,
    equation: string,
  ) {
    const formattedApiUrl =
      RoutesEnum.ORG_RANKING_PROJECT_SAVE_EQUATION.replace(
        "{project_slug}",
        project_slug,
      ).replace("{ranking_project_id}", `${ranking_project_id}`);
    return (await new ApiService().privateClient()).post<
      IApiBaseResponse<{ saved: boolean }>
    >(formattedApiUrl, { rank_name, equation });
  }

  public async getRankingProjects(project_slug: string) {
    const formattedApiUrl = RoutesEnum.ORG_RANKING_PROJECT_GET_LIST.replace(
      "{project_slug}",
      project_slug,
    );
    return (await new ApiService().privateClient()).get<
      IApiBaseResponse<IResponseRankingProjectListData>
    >(formattedApiUrl);
  }

  public async runRanking(ranking_project_id: number) {
    const formattedApiUrl = RoutesEnum.ORG_RANKING_PROJECT_RUN_RANKING.replace(
      "{ranking_project_id}",
      `${ranking_project_id}`,
    );
    return (await new ApiService().privateClient()).post<
      IApiBaseResponse<{ execution: IRankingProjectExecution | null }>
    >(formattedApiUrl, {});
  }

  public async getRankingTypes(project_slug: string) {
    const formattedApiUrl = RoutesEnum.ORG_RANKING_TYPE_GET_LIST.replace(
      "{project_slug}",
      project_slug,
    );
    return (await new ApiService().privateClient()).get<
      IApiBaseResponse<IResponseRankingTypeListData>
    >(formattedApiUrl);
  }

  public async getProjectRecordDateRange(project_slug: string) {
    const formattedApiUrl =
      RoutesEnum.ORG_RANKING_PROJECT_GET_RECORD_DATE_RANGE.replace(
        "{project_slug}",
        project_slug,
      );
    return (await new ApiService().privateClient()).get<
      IApiBaseResponse<IResponseProjectRecordDateRange>
    >(formattedApiUrl);
  }

  public async saveQuestionSnapshot(
    project_slug: string,
    ranking_project_id: number,
    questions: IRankingProjectQuestion[],
  ) {
    const formattedApiUrl =
      RoutesEnum.ORG_RANKING_PROJECT_SAVE_QUESTION_SNAPSHOT.replace(
        "{project_slug}",
        project_slug,
      ).replace("{ranking_project_id}", `${ranking_project_id}`);
    return (await new ApiService().privateClient()).post<
      IApiBaseResponse<{ saved: boolean }>
    >(formattedApiUrl, { questions });
  }

  public async getRankingTemplatesForProject(project_slug: string) {
    const formattedApiUrl =
      RoutesEnum.ORG_RANKING_PROJECT_GET_TEMPLATES.replace(
        "{project_slug}",
        project_slug,
      );
    return (await new ApiService().privateClient()).get<
      IApiBaseResponse<IResponseRankingTemplateListData>
    >(formattedApiUrl);
  }

  public async createRankingProject(
    project_slug: string,
    payload: ICreateEditRankingProjectPayload,
  ) {
    const formattedApiUrl = RoutesEnum.ORG_RANKING_PROJECT_CREATE.replace(
      "{project_slug}",
      project_slug,
    );
    return (await new ApiService().privateClient()).post<
      IApiBaseResponse<IResponseCreateEditRankingProject>
    >(formattedApiUrl, payload);
  }

  public async editRankingProject(
    project_slug: string,
    payload: ICreateEditRankingProjectPayload,
  ) {
    const formattedApiUrl = RoutesEnum.ORG_RANKING_PROJECT_EDIT.replace(
      "{project_slug}",
      project_slug,
    );
    return (await new ApiService().privateClient()).post<
      IApiBaseResponse<IResponseCreateEditRankingProject>
    >(formattedApiUrl, payload);
  }

  public async copyRankingProject(
    project_slug: string,
    ranking_project_id: number,
    new_ranking_project_title: string,
  ) {
    const formattedApiUrl = RoutesEnum.ORG_RANKING_PROJECT_COPY.replace(
      "{project_slug}",
      project_slug,
    ).replace("{ranking_project_id}", `${ranking_project_id}`);
    return (await new ApiService().privateClient()).post<
      IApiBaseResponse<IResponseCopyRankingProject>
    >(formattedApiUrl, { new_ranking_project_title });
  }
}
